import { useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Loader2, X } from 'lucide-react';

import { contactsApi } from '@/api/services/contacts.api';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { handleMutationError } from '@/lib/handle-mutation-error';
import { isTerminalBulkJob, useBulkJob } from '../hooks/use-bulk-job';
import type { IContactBulkJob } from '../types';
import { bulkJobRunningTitle, bulkJobSummary, type BulkJobKind } from '../utils/bulk-job-copy';

interface BulkJobProgressToastProps {
    readonly jobId: string;
    /** Matching count at dispatch — shown until the first poll answers. */
    readonly total: number;
    /** What the job is doing — drives the title and the finished summary. */
    readonly kind: BulkJobKind;
    readonly onClose: () => void;
}

const fmt = (n: number): string => n.toLocaleString();

/** How long a finished toast stays up — long enough to read its summary. */
export const BULK_JOB_DONE_DISMISS_MS = 6_000;
/** A failure carries an error the user must act on: give it longer. */
export const BULK_JOB_FAILED_DISMISS_MS = 15_000;

/**
 * The toast is opened with `duration: Infinity` (a job can run for minutes), so it has to
 * close itself once the job ends. `onClose` is read through a ref: callers pass an inline
 * arrow, and restarting the timer on every render would keep the toast up forever.
 */
function useDismissWhenDone(
    status: IContactBulkJob['status'] | undefined,
    onClose: () => void,
): void {
    const onCloseRef = useRef(onClose);
    useEffect(() => {
        onCloseRef.current = onClose;
    });
    useEffect(() => {
        if (!isTerminalBulkJob(status)) return;
        const delay = status === 'FAILED' ? BULK_JOB_FAILED_DISMISS_MS : BULK_JOB_DONE_DISMISS_MS;
        const timer = setTimeout(() => onCloseRef.current(), delay);
        return () => clearTimeout(timer);
    }, [status]);
}

/** `processed` can pass the dispatch-time `total` (contacts changed meanwhile): cap at 100%. */
function percentOf(job: IContactBulkJob | undefined, total: number): number {
    if (!job) return 0;
    if (job.status === 'COMPLETED') return 100;
    const base = job.total || total;
    return base > 0 ? Math.min(100, Math.round((job.processed / base) * 100)) : 0;
}

function useStatusLine(job: IContactBulkJob | undefined, total: number, kind: BulkJobKind): string {
    const { t } = useTranslation();
    if (!job || job.status === 'PENDING') {
        return t('contacts.bulkJob.queued', 'Queued — {{total}} contacts', { total: fmt(total) });
    }
    if (job.status === 'RUNNING') {
        return t('contacts.bulkJob.running', '{{processed}} of {{total}} contacts', {
            processed: fmt(Math.min(job.processed, job.total || total)),
            total: fmt(job.total || total),
        });
    }
    if (job.status === 'FAILED') {
        return job.error ?? t('contacts.bulkJob.failed', 'The bulk action failed');
    }
    return bulkJobSummary(kind, job, t);
}

/** Live progress for one background contacts bulk job, with Cancel while it runs. */
export function BulkJobProgressToast({ jobId, total, kind, onClose }: BulkJobProgressToastProps) {
    const { t } = useTranslation();
    const { data: job } = useBulkJob(jobId);
    const cancel = useMutation({
        mutationFn: () => contactsApi.cancelBulkJob(jobId),
        onError: (error: unknown) =>
            handleMutationError(error, t('contacts.bulkJob.cancelFailed', 'Could not cancel')),
    });
    const isDone = isTerminalBulkJob(job?.status);
    useDismissWhenDone(job?.status, onClose);
    const title = isDone
        ? job?.status === 'CANCELLED'
            ? t('contacts.bulkJob.cancelledTitle', 'Bulk action cancelled')
            : job?.status === 'FAILED'
              ? t('contacts.bulkJob.failedTitle', 'Bulk action failed')
              : t('contacts.bulkJob.doneTitle', 'Done — {{action}}', {
                    action: bulkJobRunningTitle(kind, t).toLowerCase(),
                })
        : bulkJobRunningTitle(kind, t);

    return (
        <div className="w-80 rounded-lg border border-border-default bg-bg-surface p-3 shadow-lg">
            <div className="mb-2 flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 text-sm font-medium text-text-heading">
                    {!isDone && <Loader2 className="size-3.5 animate-spin" />}
                    {title}
                </span>
                <button
                    type="button"
                    aria-label={t('common.close', 'Close')}
                    className="text-text-tertiary hover:text-text-heading"
                    onClick={onClose}
                >
                    <X className="size-3.5" />
                </button>
            </div>
            <Progress value={percentOf(job, total)} />
            <p className="mt-2 text-xs text-text-secondary">{useStatusLine(job, total, kind)}</p>
            {!isDone && (
                <div className="mt-2 flex justify-end">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={cancel.isPending || job?.status === undefined}
                        onClick={() => cancel.mutate()}
                    >
                        {t('common.cancel', 'Cancel')}
                    </Button>
                </div>
            )}
        </div>
    );
}
